import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { API } from "../../App";
import ProfessorLessonsList from "./ProfessorLessonsList";
import ProfessorWeather from "./ProfessorWeather";

const PAST_LIMIT = 10;

const ProfessorLessonsPage = () => {
  const [lessons, setLessons] = useState([]);
  const [enrollmentsByLesson, setEnrollmentsByLesson] = useState({});
  const [loading, setLoading] = useState(true);
  const [weatherData, setWeatherData] = useState(null);

  // Past/Future toggle
  const [showPast, setShowPast] = useState(false);
  const [pastSearch, setPastSearch] = useState("");
  const [pastPage, setPastPage] = useState(1);
  const [pastTotal, setPastTotal] = useState(0);
  const [pastPages, setPastPages] = useState(1);
  const [pastCategory, setPastCategory] = useState("all");
  const [pastTeacher, setPastTeacher] = useState("all");
  const [staffList, setStaffList] = useState([]);

  const fetchWeather = useCallback(async () => {
    try {
      const res = await fetch(`${API}/weather`);
      if (res.ok) setWeatherData(await res.json());
    } catch (err) {
      console.error("Error fetching weather:", err);
    }
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff/list`, { withCredentials: true });
      setStaffList(res.data || []);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  }, []);

  const fetchLessons = useCallback(async (overrides = {}) => {
    try {
      if (showPast || overrides.showPast) {
        const p = overrides.page ?? pastPage;
        const params = new URLSearchParams({ include_past: "true", page: p, limit: PAST_LIMIT });
        if (pastSearch) params.set("search", pastSearch);
        if (pastCategory && pastCategory !== "all") params.set("lesson_type", pastCategory);
        if (pastTeacher && pastTeacher !== "all") params.set("teacher_id", pastTeacher);
        const res = await axios.get(`${API}/lessons?${params}`, { withCredentials: true });
        setLessons(res.data.lessons || []);
        setPastTotal(res.data.total || 0);
        setPastPages(res.data.pages || 1);
      } else {
        const res = await axios.get(`${API}/lessons`, { withCredentials: true });
        const futureLessons = Array.isArray(res.data) ? res.data : (res.data.lessons || []);
        setLessons(futureLessons);
        // Prefetch enrollments for future lessons (small set)
        const allEnrollments = await axios.get(`${API}/enrollments`, { withCredentials: true });
        const byLesson = {};
        for (const e of allEnrollments.data) {
          if (!byLesson[e.lesson_id]) byLesson[e.lesson_id] = [];
          byLesson[e.lesson_id].push(e);
        }
        setEnrollmentsByLesson(byLesson);
      }
    } catch (error) {
      console.error("Error fetching lessons:", error);
    } finally {
      setLoading(false);
    }
  }, [showPast, pastPage, pastSearch, pastCategory, pastTeacher]);

  // Fetch enrollments for a specific lesson (used for past lessons on expand)
  const fetchLessonEnrollments = useCallback(async (lessonId) => {
    if (enrollmentsByLesson[lessonId]) return; // already loaded
    try {
      const res = await axios.get(`${API}/enrollments/lesson/${lessonId}`, { withCredentials: true });
      setEnrollmentsByLesson(prev => ({ ...prev, [lessonId]: res.data }));
    } catch (error) {
      console.error("Error fetching enrollments:", error);
    }
  }, [enrollmentsByLesson]);

  // Effects - after all useCallback definitions
  useEffect(() => { fetchWeather(); fetchStaff(); }, [fetchWeather, fetchStaff]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  // Debounced past search
  useEffect(() => {
    if (!showPast) return;
    const timer = setTimeout(() => {
      setPastPage(1);
      fetchLessons({ page: 1 });
    }, 400);
    return () => clearTimeout(timer);
  }, [pastSearch, showPast, fetchLessons]);

  const handleConfirmAttendance = async (enrollmentId) => {
    try {
      await axios.patch(`${API}/enrollments/${enrollmentId}`, { status: "confirmed", attended: true }, { withCredentials: true });
      toast.success("Presença confirmada!");
      refreshAfterAction();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao confirmar presença"); }
  };

  const handleCancelLesson = async (lessonId) => {
    if (!window.confirm("Cancelar esta aula? Todos os créditos serão devolvidos.")) return;
    try {
      const res = await axios.post(`${API}/lessons/${lessonId}/cancel`, {}, { withCredentials: true });
      toast.success(res.data.message);
      refreshAfterAction();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleMarkAbsent = async (enrollmentId, refund) => {
    try {
      await axios.post(`${API}/enrollments/${enrollmentId}/absent`, { refund }, { withCredentials: true });
      toast.success(refund ? "Falta marcada (crédito devolvido)" : "Falta marcada");
      refreshAfterAction();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleRevertAttendance = async (enrollmentId) => {
    if (!window.confirm("Reverter a presença deste aluno? O crédito será devolvido se aplicável.")) return;
    try {
      const res = await axios.patch(`${API}/enrollments/${enrollmentId}/revert-attendance`, {}, { withCredentials: true });
      toast.success(res.data.message);
      refreshAfterAction();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reverter presença"); }
  };

  // Re-fetch enrollments for a specific lesson (after adding student)
  const refreshLessonEnrollments = useCallback(async (lessonId) => {
    try {
      const res = await axios.get(`${API}/enrollments/lesson/${lessonId}`, { withCredentials: true });
      setEnrollmentsByLesson(prev => ({ ...prev, [lessonId]: res.data }));
    } catch (err) {
      console.error("Error refreshing enrollments:", err);
    }
    fetchLessons();
  }, [fetchLessons]);

  // After any action, refresh enrollments for the affected lesson
  const refreshAfterAction = useCallback(() => {
    setEnrollmentsByLesson({}); // Clear cache so next expand re-fetches
    fetchLessons();
  }, [fetchLessons]);

  const handleTogglePast = (past) => {
    setShowPast(past);
    setPastPage(1);
    setPastSearch("");
    setPastCategory("all");
    setPastTeacher("all");
    setEnrollmentsByLesson({});
    setLoading(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#3d5a63] mb-1" data-testid="prof-lessons-title">Aulas</h1>
        <p className="text-sm text-[#475569]">
          {showPast ? "Histórico de aulas" : "Aulas futuras e gestão de presenças"}
        </p>
      </div>

      {/* Toggle + Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex bg-slate-100 rounded-lg p-0.5 shrink-0" data-testid="prof-lessons-toggle">
          <button
            onClick={() => handleTogglePast(false)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${!showPast ? "bg-white text-[#3d5a63] shadow-sm" : "text-[#94A3B8]"}`}
            data-testid="prof-toggle-future"
          >
            Futuras
          </button>
          <button
            onClick={() => handleTogglePast(true)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${showPast ? "bg-white text-[#3d5a63] shadow-sm" : "text-[#94A3B8]"}`}
            data-testid="prof-toggle-past"
          >
            Passadas
          </button>
        </div>

        {showPast && (
          <>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
              <Input
                placeholder="Pesquisar aulas..."
                value={pastSearch}
                onChange={(e) => setPastSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="prof-search-lessons"
              />
            </div>
            <select
              value={pastCategory}
              onChange={(e) => { setPastCategory(e.target.value); setPastPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="prof-filter-category"
            >
              <option value="all">Todas</option>
              <option value="group">Grupo</option>
              <option value="private">Privadas</option>
            </select>
            {staffList.length > 0 && (
              <select
                value={pastTeacher}
                onChange={(e) => { setPastTeacher(e.target.value); setPastPage(1); }}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="prof-filter-teacher"
              >
                <option value="all">Todos os professores</option>
                {staffList.map((s) => (
                  <option key={s.user_id} value={s.user_id}>{s.name || s.email}</option>
                ))}
              </select>
            )}
          </>
        )}
      </div>

      {!showPast && <ProfessorWeather weatherData={weatherData} />}

      <ProfessorLessonsList
        lessons={lessons}
        enrollmentsByLesson={enrollmentsByLesson}
        onConfirmAttendance={handleConfirmAttendance}
        onMarkAbsent={handleMarkAbsent}
        onRevertAttendance={handleRevertAttendance}
        onCancelLesson={handleCancelLesson}
        onExpandLesson={fetchLessonEnrollments}
        onRefreshEnrollments={refreshLessonEnrollments}
        showPast={showPast}
      />

      {/* Pagination for past */}
      {showPast && pastPages > 1 && (
        <div className="flex items-center justify-between px-1" data-testid="prof-past-pagination">
          <p className="text-xs text-[#94A3B8]">{pastTotal} aulas encontradas</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8" disabled={pastPage <= 1} onClick={() => setPastPage(p => p - 1)} data-testid="prof-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#475569]">{pastPage}/{pastPages}</span>
            <Button size="sm" variant="outline" className="h-8" disabled={pastPage >= pastPages} onClick={() => setPastPage(p => p + 1)} data-testid="prof-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessorLessonsPage;
